'use client'

import { type FC } from 'react'

import { AdServerExperimentActivation } from '../ad-server'
import { AppsFlyerBanner } from '../apps-flyer-banner'
import { CartIntermediateContextProvider } from '../cart-intermediate'
import { CrossSellConfigProvider, CrossSellDialogDynamic } from '../cross-sell'
import { ErrorBoundary } from '../error-boundary'
import { ExperimentFreeGiftPromotionActivation } from '../experiments/components/ExperimentFreeGiftPromotion/ExperimentFreeGiftPromotionActivation'
import { ExperimentOwnBrandsOverlay } from '../experiments/components/ExperimentOwnBrandsOverlay/ExperimentOwnBrandsOverlay'
import { isProductInOwnBrandsProductsMap } from '../experiments/components/ExperimentOwnBrandsOverlay/isProductInOwnBrandsProductsMap'
import { ExperimentsPageTypeProvider } from '../experiments/components/ExperimentsPageTypeContext'
import { ExperimentPageType } from '../experiments/ExperimentPageType'
import {
  useGetExperimentForTenant,
  useSetSearchSessionStorageValuesForOptimizelyTracking,
} from '../experiments/hooks'
import { EXPERIMENT_OWN_BRANDS_OVERLAY } from '../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { FirstImpressionProvider } from '../first-impression'
import { GkAirConfigProvider } from '../gk-air'
import { useGlobalConfigContext } from '../global-config'
import { NowGeneralContextProvider } from '../now'
import { SellerModalProvider } from '../seller'
import { StandardShopLayout } from '../standard-shop-layout'

import { ProductBreadcrumbs } from './components/ProductBreadcrumbs/ProductBreadcrumbs'
import { ProductPageAdServerPushCommand } from './components/ProductPageAdServerPushCommand'
import { ProductPageContextProvider } from './components/productPageContext'
import { ProductPageOverview } from './components/ProductPageCswPosition'
import { ProductPageJsonLdProductDescription } from './components/ProductPageJsonLdProductDescription/ProductPageJsonLdProductDescription'
import { ProductPageLatestActivity } from './components/ProductPageLatestActivity'
import { type ProductPageProps } from './components/ProductPageProps'
import { ProductOrderLimitContextProvider } from './product-order-limit/components/productOrderLimitContext'
import { ProductPageSeoHeadTags } from './seo/components/ProductPageSeoHeadTags/ProductPageSeoHeadTags'

export const ProductPageContent: FC<ProductPageProps> = ({
  breadcrumbs,
  crossSellConfig,
  firstImpression,
  gkAirConfig,
  productPageConfig,
  productPageData,
  standardLayoutData,
}) => {
  const ownBrandsLayoutExperiment = useGetExperimentForTenant(
    EXPERIMENT_OWN_BRANDS_OVERLAY,
  )

  const { tenant } = useGlobalConfigContext()

  useSetSearchSessionStorageValuesForOptimizelyTracking()

  /* eslint-disable react/jsx-max-depth -- the rule was disabled before a comment became required */
  return (
    <ExperimentsPageTypeProvider pageType={ExperimentPageType.Product}>
      <StandardShopLayout standardLayoutData={standardLayoutData}>
        <AppsFlyerBanner config={productPageData.appsFlyerBannerConfig} />
        <NowGeneralContextProvider>
          <FirstImpressionProvider firstImpression={firstImpression}>
            <ProductOrderLimitContextProvider
              config={productPageData.productOrderLimitConfig}
            >
              <ProductPageContextProvider
                breadcrumbs={breadcrumbs}
                channel={productPageData.channel}
                faqs={productPageData.faqs}
                followUpProduct={productPageData.followUpProduct}
                pageParams={productPageData.pageParams}
                product={productPageData.product}
                productPageConfig={productPageConfig}
                productPageOdsConfig={productPageData.productPageOdsConfig}
                promotionProduct={productPageData.promotionProduct}
                reviewData={productPageData.reviewData}
                shipping={productPageData.shipping}
                socialMediaConfig={productPageData.socialMediaConfig}
              >
                <ExperimentFreeGiftPromotionActivation />
                {ownBrandsLayoutExperiment.isEnabled &&
                isProductInOwnBrandsProductsMap(
                  productPageData.product.upid,
                  tenant,
                ) ? (
                  <ExperimentOwnBrandsOverlay
                    experimentVariation={ownBrandsLayoutExperiment.variant}
                    ownBrandsProduct={
                      productPageData.experimentOwnBrandsOverlayProduct
                    }
                  />
                ) : null}
                <AdServerExperimentActivation />
                <ProductPageSeoHeadTags />
                <CrossSellConfigProvider value={crossSellConfig}>
                  <GkAirConfigProvider value={gkAirConfig}>
                    <SellerModalProvider>
                      <CartIntermediateContextProvider
                        pdpGtmData={productPageData.gtmDataProps}
                      >
                        <ProductPageAdServerPushCommand />
                        <ProductPageJsonLdProductDescription />
                        <ProductBreadcrumbs />
                        <div
                          className="container pt-5"
                          data-qa-id="product-details-page"
                        >
                          <ProductPageOverview />
                          <ErrorBoundary>
                            <ProductPageLatestActivity />
                          </ErrorBoundary>
                        </div>
                        <CrossSellDialogDynamic />
                      </CartIntermediateContextProvider>
                    </SellerModalProvider>
                  </GkAirConfigProvider>
                </CrossSellConfigProvider>
              </ProductPageContextProvider>
            </ProductOrderLimitContextProvider>
          </FirstImpressionProvider>
        </NowGeneralContextProvider>
      </StandardShopLayout>
    </ExperimentsPageTypeProvider>
  )
  /* eslint-enable react/jsx-max-depth */
}
